// src/components/stages/preconsult/PreConsultPatientHero.tsx
// Replaces PreConsultStage.tsx's old `VitalsThisVisit` strip and the Risk
// level / Pain scale portion of its "Triage" SectionCard — see
// docs/HMS_V4_PRECONSULT_REDESIGN_PLAN.md Phase 1.2. Same vitals, same
// thresholds (lib/vitalThresholds.ts), same ADVISORY_NOTE; Risk level and
// Pain scale stay fully editable here — this is a placement change, not a
// change of what Pre-Consult records.
import { VitalTile } from '../../ui/VitalTile';
import { StatTile } from '../../ui/StatTile';
import { SegmentedControl } from '../../ui/SegmentedControl';
import { RISK_LEVEL_TONE } from '../../ui/RiskBadge';
import { RISK_LEVELS, PAIN_SCALE_OPTIONS, type RiskLevel } from '../TriageFieldsEditor';
import { checkBp, checkPulse, checkTemp, checkSpo2, checkBmi, ADVISORY_NOTE } from '../../../lib/vitalThresholds';
import type { Patient } from '../../../types/visit';
import { cn } from '../../../lib/cn';
import { patientDisplayName } from '../../../lib/patientName';

export interface PreConsultVitalsSnapshot {
  bp_sys?: string | number; bp_dia?: string | number; pulse?: string | number;
  temp?: string | number; spo2?: string | number; weight?: string | number;
  height?: string | number; bmi?: string | number; bloodGroup?: string;
  symptoms?: string;
}

/** "Last visit: 12 Jun 2026" — omitted when there isn't one (a first visit).
 * Deliberately not de-duped against today the way PatientBanner's own
 * `lastVisitLabel` is: `patient.last_visit_at` here is whatever the visit
 * payload carries, and this hero is a supplementary identity strip, not the
 * banner's own "don't repeat today" contract. */
function lastVisitLabel(patient: Patient | null): string | null {
  const raw = patient?.last_visit_at;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return `Last visit: ${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`;
}

export interface PreConsultPatientHeroProps {
  patient: Patient | null;
  vitals: PreConsultVitalsSnapshot | undefined;
  riskLevel: RiskLevel;
  onRiskLevelChange: (v: RiskLevel) => void;
  painScale: string;
  onPainScaleChange: (v: string) => void;
  /** Whether this ailment template wants each field — mirrors
   * TriageFieldsEditor's own `enabledFields` gate (e.g. a Diabetes Assessment
   * has no reason to ask a pain scale). */
  showRiskLevel: boolean;
  showPainScale: boolean;
  disabled?: boolean;
}

export function PreConsultPatientHero({
  patient, vitals, riskLevel, onRiskLevelChange, painScale, onPainScaleChange, showRiskLevel, showPainScale, disabled,
}: PreConsultPatientHeroProps) {
  const bp = vitals?.bp_sys && vitals?.bp_dia ? `${vitals.bp_sys}/${vitals.bp_dia}` : null;
  const hasVitals = Boolean(bp || vitals?.pulse || vitals?.temp || vitals?.spo2 || vitals?.weight || vitals?.height || vitals?.bmi || vitals?.bloodGroup);
  const last = lastVisitLabel(patient);
  const showTriageRow = hasVitals || showRiskLevel || showPainScale;

  return (
    <div className="rounded-[var(--radius)] border border-line bg-card p-3.5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-full bg-muted text-2xl text-ink-muted">
            👤
          </div>
          <div>
            <div className="text-[16px] font-bold text-ink">{patientDisplayName(patient) || 'Unnamed patient'}</div>
            <div className="text-[11.5px] text-ink-muted">
              {patient?.age != null ? patient.age : '—'} / {patient?.gender || '—'}
              {patient?.uhid ? ` · ID ${patient.uhid}` : ''}
            </div>
          </div>
        </div>
        {(last || patient?.uhid) && (
          <div className="text-right text-[10.5px] leading-relaxed text-ink-muted">
            {last && <div>{last}</div>}
            {patient?.uhid && <div>UHID: {patient.uhid}</div>}
          </div>
        )}
      </div>

      {showTriageRow && (
        <div className="mt-3.5 border-t border-line pt-3">
          {hasVitals && (
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <div className="text-[10.5px] font-bold uppercase tracking-wide text-ink-muted">Vitals this visit · read-only</div>
              {/* A printed cut-off must carry the word advisory, or it reads as a diagnosis — lib/vitalThresholds.ts's own condition. */}
              <div className="text-[10px] text-ink-muted">{ADVISORY_NOTE}</div>
            </div>
          )}
          <div className={cn('flex flex-wrap items-start gap-x-6 gap-y-3', hasVitals && 'justify-between')}>
            {hasVitals && (
              <div className="flex flex-1 flex-wrap gap-2">
                <VitalTile icon="🫀" label="BP" value={bp} unit="mmHg" check={checkBp(vitals?.bp_sys, vitals?.bp_dia)} />
                <VitalTile icon="💓" label="PR" value={vitals?.pulse} unit="bpm" check={checkPulse(vitals?.pulse)} />
                <VitalTile icon="🌡️" label="Temp" value={vitals?.temp} unit="°F" check={checkTemp(vitals?.temp)} />
                <VitalTile icon="🫁" label="SpO₂" value={vitals?.spo2} unit="%" check={checkSpo2(vitals?.spo2)} />
                <StatTile icon="📏" label="Height" value={vitals?.height} unit="cm" />
                <StatTile icon="⚖️" label="Weight" value={vitals?.weight} unit="kg" />
                <VitalTile icon="📊" label="BMI" value={vitals?.bmi} check={checkBmi(vitals?.bmi)} />
                <StatTile icon="🩸" label="Blood group" value={vitals?.bloodGroup} />
              </div>
            )}

            {(showRiskLevel || showPainScale) && (
              <div className="flex shrink-0 flex-col items-end gap-2">
                {showRiskLevel && (
                  <div>
                    <div className="mb-1 text-right text-[9.5px] font-bold uppercase tracking-wide text-ink-muted">Risk level</div>
                    <SegmentedControl
                      size="sm"
                      options={RISK_LEVELS.map(r => ({ value: r, label: r, tone: RISK_LEVEL_TONE[r] as 'success' | 'warn' | 'caution' | 'danger' | 'info' }))}
                      value={riskLevel}
                      onChange={onRiskLevelChange}
                      disabled={disabled}
                    />
                  </div>
                )}
                {showPainScale && (
                  <div>
                    <div className="mb-1 text-right text-[9.5px] font-bold uppercase tracking-wide text-ink-muted">Pain scale (0-10)</div>
                    <SegmentedControl
                      size="sm"
                      options={PAIN_SCALE_OPTIONS.map(n => ({ value: n, label: n }))}
                      value={painScale}
                      onChange={onPainScaleChange}
                      disabled={disabled}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          {vitals?.symptoms && (
            <div className="mt-2 text-[12.5px] text-ink-muted">
              <b className="text-ink">Symptoms:</b> {vitals.symptoms}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
